import React from "react";
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar,
  Tooltip, XAxis, YAxis, ResponsiveContainer, CartesianGrid,
} from "recharts";
import "./ChartsSection.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartLine, faChartArea, faChartBar,
  faCalendarCheck, faClock, faBed,
} from "@fortawesome/free-solid-svg-icons";

interface ChartsSectionProps {
  revenue30: { date: string; total: number }[];
  occupancyTrend: { date: string; percent: number }[];
  bookingPace: { date: string; count: number }[];
  cancellations30: { date: string; count: number }[];
  leadTime: number[];
  revenueByType: Record<string, number>;
}

const TOOLTIP_STYLE = {
  contentStyle: {
    background: "#0E1E38",
    border: "1px solid #e2e8f0",
    borderRadius: "10px",
    fontSize: "12px",
    color: "#F2EBD9",
    boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
  },
  labelStyle: { color: "rgba(242,235,217,0.4)", marginBottom: 4 },
  cursor: { stroke: "rgba(201,168,76,0.25)", strokeWidth: 1 },
};

const TICK = { fontSize: 11, fill: "#94a3b8" };
const GRID = { strokeDasharray: "3 3", stroke: "rgba(0,0,0,0.06)" };

function ChartCard({ icon, title, color, children }: {
  icon: any; title: string; color: string; children: React.ReactNode;
}) {
  return (
    <div className="cc-card">
      <div className="cc-card-header">
        <div className="cc-icon-wrap" style={{ background: color + "20", color }}>
          <FontAwesomeIcon icon={icon} />
        </div>
        <h3 className="cc-title">{title}</h3>
      </div>
      {children}
    </div>
  );
}

export default function ChartsSection({
  revenue30, occupancyTrend, bookingPace, cancellations30, leadTime, revenueByType,
}: ChartsSectionProps) {
  const leadTimeData = leadTime.map((v, i) => ({ id: i + 1, days: v }));
  const revenueByTypeArray = Object.entries(revenueByType).map(([type, total]) => ({
    type, total: Number(total) || 0,
  }));

  return (
    <div className="cc-grid">

      {/* Chart 1 — Monthly Revenue */}
      <ChartCard icon={faChartLine} title="Monthly Revenue (Last 30 Days)" color="#10b981">
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={revenue30}>
            <defs>
              <linearGradient id="revGrad" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#10b981" />
                <stop offset="100%" stopColor="#34d399" />
              </linearGradient>
            </defs>
            <CartesianGrid {...GRID} />
            <XAxis dataKey="date" tick={TICK} tickLine={false} axisLine={false} />
            <YAxis tick={TICK} tickLine={false} axisLine={false} />
            <Tooltip {...TOOLTIP_STYLE} formatter={(v: any) => [`EGP ${Number(v).toLocaleString()}`, "Revenue"]} />
            <Line type="monotone" dataKey="total" stroke="url(#revGrad)" strokeWidth={2.5} dot={false} activeDot={{ r: 4, fill: "#10b981" }} />
          </LineChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* Chart 2 — Occupancy Trend */}
      <ChartCard icon={faChartArea} title="Occupancy Trend (7-Day Rolling)" color="#6366f1">
        <ResponsiveContainer width="100%" height={220}>
          <AreaChart data={occupancyTrend}>
            <defs>
              <linearGradient id="occGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#6366f1" stopOpacity={0.4} />
                <stop offset="100%" stopColor="#6366f1" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid {...GRID} />
            <XAxis dataKey="date" tick={TICK} tickLine={false} axisLine={false} />
            <YAxis tick={TICK} tickLine={false} axisLine={false} />
            <Tooltip {...TOOLTIP_STYLE} formatter={(v: any) => [`${v}%`, "Occupancy"]} />
            <Area type="monotone" dataKey="percent" stroke="#6366f1" fill="url(#occGrad)" strokeWidth={2.5} dot={false} activeDot={{ r: 4 }} />
          </AreaChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* Chart 3 — Booking Pace */}
      <ChartCard icon={faChartBar} title="Booking Pace (Last 7 Days)" color="#3b82f6">
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={bookingPace} barSize={20}>
            <CartesianGrid {...GRID} />
            <XAxis dataKey="date" tick={TICK} tickLine={false} axisLine={false} />
            <YAxis tick={TICK} tickLine={false} axisLine={false} />
            <Tooltip {...TOOLTIP_STYLE} formatter={(v: any) => [`${v}`, "Bookings"]} />
            <Bar dataKey="count" fill="#3b82f6" radius={[4,4,0,0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* Chart 4 — Cancellations */}
      <ChartCard icon={faCalendarCheck} title="Cancellations (Last 30 Days)" color="#ef4444">
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={cancellations30} barSize={20}>
            <CartesianGrid {...GRID} />
            <XAxis dataKey="date" tick={TICK} tickLine={false} axisLine={false} />
            <YAxis tick={TICK} tickLine={false} axisLine={false} />
            <Tooltip {...TOOLTIP_STYLE} formatter={(v: any) => [`${v}`, "Cancelled"]} />
            <Bar dataKey="count" fill="#ef4444" radius={[4,4,0,0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* Chart 5 — Lead Time */}
      <ChartCard icon={faClock} title="Lead Time Distribution" color="#f59e0b">
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={leadTimeData}>
            <CartesianGrid {...GRID} />
            <XAxis dataKey="id" tick={TICK} tickLine={false} axisLine={false} />
            <YAxis tick={TICK} tickLine={false} axisLine={false} />
            <Tooltip {...TOOLTIP_STYLE} formatter={(v: any) => [`${v} days`, "Lead Time"]} />
            <Line type="monotone" dataKey="days" stroke="#f59e0b" strokeWidth={2.5} dot={false} activeDot={{ r: 4 }} />
          </LineChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* Chart 6 — Revenue by Room Type */}
      <ChartCard icon={faBed} title="Revenue by Room Type" color="#8b5cf6">
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={revenueByTypeArray} barSize={28}>
            <CartesianGrid {...GRID} />
            <XAxis dataKey="type" tick={TICK} tickLine={false} axisLine={false} />
            <YAxis tick={TICK} tickLine={false} axisLine={false} />
            <Tooltip {...TOOLTIP_STYLE} formatter={(v: any) => [`EGP ${Number(v).toLocaleString()}`, "Revenue"]} />
            <Bar dataKey="total" fill="#8b5cf6" radius={[4,4,0,0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

    </div>
  );
}
